import React, { useState, useEffect, useRef } from 'react';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { toast } from 'react-hot-toast';
import { supabase } from '../../lib/supabase';
import { enhancedRecommendationService } from '../../services/enhancedRecommendationService';
import { recommendationLetterService } from '../../services/recommendationLetterService';
import {
  emailTemplates,
  recommendationStrategy,
  getTimelineWarning
} from './recommendationFramework';
import {
  ArrowLeft,
  Send,
  Bot,
  Copy,
  ExternalLink,
  CheckCircle,
  Clock,
  Sparkles,
  Award,
  FileText,
  Zap,
  Download,
  Edit3,Edit2,
  X
} from 'lucide-react';
import './SmartRecRequestFlow.css';

const EnhancedSmartRecRequestFlow = ({
  recommenderId,
  recommendationData = null,
  emailType = 'request', // 'request', 'followup', or 'thankyou'
  onBack,
  onComplete
}) => {
  console.log('EnhancedSmartRecRequestFlow rendered with:', {
    recommenderId,
    hasRecommendationData: !!recommendationData,
    recommenderName: recommendationData?.recommender_name
  });
  const { currentUser } = useFamilyContext();
  const [currentStep, setCurrentStep] = useState(0);
  const [responses, setResponses] = useState({});
  const [conversationFlow, setConversationFlow] = useState([]);
  const [relationshipLevel, setRelationshipLevel] = useState(30);
  const [recommenderData, setRecommenderData] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [generatedEmail, setGeneratedEmail] = useState('');
  const [editableEmail, setEditableEmail] = useState('');
  const [isGeneratingEmail, setIsGeneratingEmail] = useState(false);
  const [sliderConfirmed, setSliderConfirmed] = useState(false);
  const [showSliderConfirmation, setShowSliderConfirmation] = useState(false);
  const [sliderFeedback, setSliderFeedback] = useState('');
  const [feedbackMessageIndex, setFeedbackMessageIndex] = useState(null);
  const [needsRecommenderInfo, setNeedsRecommenderInfo] = useState(false);
  const [tempRecommenderInfo, setTempRecommenderInfo] = useState({
    name: '',
    email: '',
    type: 'teacher',
    subject: '',
    relationship_length: '',
    colleges: [],
    due_date: (() => {
      const date = new Date();
      date.setDate(date.getDate() + 45);
      return date.toISOString().split('T')[0];
    })(),
    notes: ''
  });
  const [infoCollectionStep, setInfoCollectionStep] = useState(0);
  const [currentPhase, setCurrentPhase] = useState('assessment'); // assessment, template, email
  const [aiAssessment, setAiAssessment] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [personalContext, setPersonalContext] = useState({});
  const [frameworkGuidance, setFrameworkGuidance] = useState(null);
  const [messages, setMessages] = useState([]);
  const [editingMessageIndex, setEditingMessageIndex] = useState(null);

  const chatMessagesRef = useRef(null);
  const inputRef = useRef(null);

  // Enhanced conversation flows that map to framework template fields
  const conversationFlows = {
    teacher: {
      low: [
        {
          question: "What's something you did in their class that they'd probably remember?",
          suggestions: [
            "I was the only student who actually solved the extra-credit challenge on the board",
            "I brought my dog in as part of my science project",
            "I tutored a classmate every day before school",
            "I made the Romeo & Juliet poster you kept as an example for future classes"
          ],
          field: "classroom_contribution",
          templateMapping: "growth_moment"
        },
        {
          question: "Best moment from their class?",
          suggestions: [
            "The lab experiment we did on the last day of school",
            "The ethical debate we had that was so good you let us spend the whole week on it",
            "The day my paper ended up being used as the class example",
            "Finally grasping a concept I had struggled with all semester"
          ],
          field: "strongest_memory",
          templateMapping: "specific_memory"
        },
        {
          question: "Why did you pick this teacher?",
          suggestions: [
            "They noticed my effort even if we didn’t talk much",
            "They saw how much I improved from the beginning of the year",
            "They’re consistent and fair, which made it easier to succeed",
            "They respected students’ work ethic more than personality"
          ],
          field: "selection_reason",
          templateMapping: "academic_strength"
        }
      ],
      medium: [
        {
          question: "What's something they'd remember about you?",
          suggestions: ["I ask good questions", "I'm creative with assignments", "I never give up", "I always engaged"],
          field: "memorable_trait",
          templateMapping: "character_trait"
        },
        {
          question: "Best memory from their class?",
          suggestions: ["The ethical debate we had that was so good you let us spend the whole week on it","When they stayed after school to talk through my project idea", "My group presentation that went so wrong it was hilarious", "When they helped me through something hard"],
          field: "standout_moment",
          templateMapping: "specific_memory"
        },
        {
          question: "How did you grow?",
          suggestions: ["I went from struggling to excelling", "I finally got comfortable participating in group discussion", "I came out of that class with much stronger writing skills", "I learned to push myself out of my comfort zone"],
          field: "growth_description",
          templateMapping: "growth_moment"
        }
      ],
      high: [
        {
          question: "Tell me something they said or did that really stuck with you.",
  suggestions: [
    "They told me I had potential I didn’t see in myself",
    "They noticed the week I was really struggling and pulled me aside to talk",
    "They made me the group leader for the final project even though I hadn’t led before",
    "They told me my work was strong enough to compete at the next level and pushed me to try"
  ],
  field: "memorable_interaction",
  templateMapping: "specific_memory"
},
{
  question: "What's something you got out of the class?",
  suggestions: [
    "I started enjoying hard problems instead of avoiding them",
    "I gained confidence in sharing my ideas (even the weird ones)",
    "I discovered that I actually like reading once I found books that challenged me",
    "I realized that this is the subject I want to pursue in college"
  ],
  field: "observed_transformation",
  templateMapping: "growth_moment"
},
{
  question: "What do they know about the real you?",
  suggestions: [
    "I care about doing things right",
    "I’m curious about how things work",
    "I try to make people feel included",
    "I don’t give up no matter how hard it is"
  ],
  field: "character_assessment",
  templateMapping: "character_trait"
},
      ]
    },
    counselor: {
      medium: [
        {
          question: "How do they know you?",
          suggestions: ["College planning meetings", "Course selection help", "Academic goal setting", "Personal challenges"],
          field: "counseling_context",
          templateMapping: "personal_discussion"
        },
        {
          question: "What are you proud of?",
          suggestions: ["I overcame academic challenges", "My leadership in activities", "Balancing work and school", "Personal growth"],
          field: "proud_moment",
          templateMapping: "proud_achievement"
        },
        {
          question: "Is there anything on your transcript you'd want to point out or explain to them?",
          suggestions: ["Had a rough semester junior year due to [family/health/etc.]", "I got permission to bypass prerequisites and still did really well", "I took every English class that was available at the high school", "I took a light course schedule senior year because I had an internship", "Not really"],
          field: "challenge_overcome",
          templateMapping: "biggest_challenge"
        }
      ]
    },
    coach_advisor: {
      medium: [
        {
          question: "What do you do together?",
          suggestions: ["Varsity Basketball - point guard", "Drama - lead role", "Student Gov - committee member", "Debate team - captain"],
          field: "activity_role",
          templateMapping: "subject"
        },
        {
          question: "When did you step up?",
          suggestions: ["When no one else volunteered to go first", "I took it upon myself to mentor new members", "I organized two successful fundraisers", "I am good at motivating teammates when they feel discouraged"],
          field: "leadership_growth",
          templateMapping: "leadership_example"
        },
        {
          question: "What would they say you bring to the team?",
          suggestions: ["Positive energy", "I way I make everyone feel included", "I'll step up wherever I'm needed", "I never run out of new ideas"],
          field: "team_contribution"
        },
        {
          question: "What was the best moment you had with them?",
          suggestions: ["Breaking my PR after taking their advice", "The time they pulled me aside for a pep talk when I was ready to quit", "Going to nationals", "When they gave me a shot even if I wasn't sure I could do it"],
          field: "character_moment"
        }
      ]
    },

    // Follow-up Email Flow
    followup: [
      {
        question: "What materials have you prepared to share with them?",
        suggestions: ["Brag sheet and resume", "Personal statement and college list", "Complete application packet", "Everything they requested"],
        field: "materials",
        templateMapping: "materials"
      },
      {
        question: "Any recent achievements or updates since you first reached out?",
        suggestions: ["Won an academic competition", "Got a leadership position", "Improved my grades", "Nothing major"],
        field: "updates",
        templateMapping: "updates"
      },
      {
        question: "Are there specific points or themes you'd like them to emphasize?",
        suggestions: ["My growth in their subject", "Leadership qualities", "Academic curiosity", "Work ethic and determination"],
        field: "specific_points",
        templateMapping: "specific_points"
      }
    ],

    // Thank You Email Flow
    thankyou: [
      {
        question: "What's your college outcome to share?",
        suggestions: ["Got into my reach school!", "Multiple acceptances to share", "Got a great scholarship", "Still waiting but optimistic"],
        field: "outcome",
        templateMapping: "outcome"
      },
      {
        question: "How did their letter help your application?",
        suggestions: ["It highlighted my strengths perfectly", "They captured who I really am", "Their endorsement was invaluable", "They went above and beyond"],
        field: "impact",
        templateMapping: "impact"
      },
      {
        question: "What are your plans for next year?",
        suggestions: ["Heading to [specific college] for [major]", "Taking a gap year to [activity]", "Starting my journey in [field of study]", "Exploring my options"],
        field: "future",
        templateMapping: "future"
      }
    ],

    // Old Thank You Note Flows (kept for backwards compatibility)
    teacher_thank_you: {
      questions: [
        {
          question: "What's your news?",
          suggestions: ["Got into my top choice!", "Got a scholarship!", "Multiple acceptances!", "Early admission!"],
          field: "outcome_update",
          templateMapping: "outcome_update"
        },
        {
          question: "Anything else you want to mention?",
          suggestions: ["They really changed how I think about [subject]", "Just want to say thanks for believing in me", "Their class was actually fun", "Nope! Let's keep it short and sweet."],
          field: "memorable_moment",
          templateMapping: "memorable_moment",
          optional: true
        }
      ]
    },
    
    counselor_thank_you: {
      questions: [
        {
          question: "What's your news?",
          suggestions: ["Got into my top choice!", "Got a scholarship!", "Found the perfect fit school", "Multiple acceptances!"],
          field: "outcome_update",
          templateMapping: "outcome_update"
        },
        {
          question: "Anything else you want to mention?",
          suggestions: ["Thanks for helping me find the right schools", "Your support meant everything", "You helped me believe in myself", "Nope! Let's keep it short and sweet."],
          field: "counseling_support",
          templateMapping: "counseling_support",
          optional: true
        }
      ]
    },

    coach_advisor_thank_you: {
      questions: [
        {
          question: "What's your news?",
          suggestions: ["Got into my top choice!", "Got an athletic scholarship!", "Will continue my sport in college", "Multiple acceptances!"],
          field: "outcome_update",
          templateMapping: "outcome_update"
        },
        {
          question: "Anything else you want to mention?",
          suggestions: ["You taught me about leadership", "Thanks for believing in me when I didn't", "Best coach I've ever had", "Nope! Let's keep it short and sweet."],
          field: "impact_statement",
          templateMapping: "impact_statement",
          optional: true
        }
      ]
    },

    employer_mentor: {
      medium: [
        {
          question: "What's your role?",
          suggestions: ["Part-time retail", "Nonprofit volunteer", "Summer intern", "Research assistant"],
          field: "work_role",
          templateMapping: "subject"
        },
        {
          question: "What's the best part of working there?",
          suggestions: ["Great team to work with", "Learning so much", "They trust me with real work", "Actually making a difference"],
          field: "work_initiative"
        },
        {
          question: "Toughest moment?",
          suggestions: ["Found creative solution", "Handled busy period", "Learned from mistake", "Dealt with difficult customer"],
          field: "problem_solving"
        },
        {
          question: "How did you grow?",
          suggestions: ["More confident communicating", "Developed work ethic", "Learned to juggle priorities", "Became a leader"],
          field: "professional_growth"
        }
      ]
    }
  };

  // Initialize with existing recommendation data if editing
  useEffect(() => {
    if (recommendationData) {
      // Use directly passed recommendation data
      populateWithRecommendationData(recommendationData);
    } else if (recommenderId && recommenderId !== null) {
      // Fetch recommendation data by ID
      loadExistingRecommendation();
    } else {
      // Check if we have a name from recommendation context before showing form
      setNeedsRecommenderInfo(true);
    }
  }, [recommenderId, recommendationData]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Escape key to close modal
      if (event.key === 'Escape') {
        onBack();
        return;
      }

      // Enter key for various interactions
      if (event.key === 'Enter' && !event.shiftKey) {
        // Don't interfere if user is typing in textarea or input
        if (event.target.tagName === 'TEXTAREA' || event.target.tagName === 'INPUT') {
          return;
        }

        // If slider feedback is showing, confirm it
        if (showSliderConfirmation) {
          event.preventDefault();
          confirmSliderAssessment();
          return;
        }

        // If quick response buttons are available, select the first one
        const quickResponseBtns = document.querySelectorAll('.quick-response-btn');
        if (quickResponseBtns.length > 0 && !inputValue.trim()) {
          event.preventDefault();
          quickResponseBtns[0].click();
          return;
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onBack, showSliderConfirmation, inputValue]);

  // Auto-scroll to latest message when messages update
  useEffect(() => {
    if (chatMessagesRef.current && messages.length > 0) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
      }, 100);
    }
  }, [messages]);

  // Reset textarea height when input is cleared
  useEffect(() => {
    if (inputRef.current && !inputValue) {
      inputRef.current.style.height = 'auto';
    }
  }, [inputValue]);

  const populateWithRecommendationData = (existingRec) => {
    try {
      console.log('Populating with recommendation data:', existingRec);

      // Populate the form with existing data
      setTempRecommenderInfo({
        name: existingRec.recommender_name || '',
        email: existingRec.recommender_email || '',
        type: existingRec.recommender_type || 'teacher',
        subject: existingRec.subject || '',
        relationship_length: existingRec.relationship_length || '',
        colleges: existingRec.colleges || [],
        due_date: existingRec.due_date ?
          new Date(existingRec.due_date).toISOString().split('T')[0] :
          (() => {
            const date = new Date();
            date.setDate(date.getDate() + 45);
            return date.toISOString().split('T')[0];
          })(),
        notes: existingRec.notes || ''
      });

      // Skip the info collection
      setNeedsRecommenderInfo(false);

      // For follow-ups and thank yous, skip relationship assessment
      if (emailType === 'followup' || emailType === 'thankyou') {
        let welcomeContent;
        if (emailType === 'followup') {
          welcomeContent = `Perfect! Let's draft the follow-up email to ${existingRec.recommender_name} with your materials.`;
        } else {
          welcomeContent = `Wonderful! Let's create a thoughtful thank you note for ${existingRec.recommender_name}.`;
        }

        const welcomeMessage = {
          type: 'bot',
          content: welcomeContent,
          timestamp: Date.now()
        };

        setMessages([welcomeMessage]);
        setSliderConfirmed(true); // Skip slider
        setRelationshipLevel(75); // Default to medium-high for follow-ups

        // Start conversation flow directly
        setTimeout(() => {
          const assessment = generateAiAssessment();
          setAiAssessment(assessment);

          const flow = emailType === 'followup' ? conversationFlows.followup : conversationFlows.thankyou;
          setConversationFlow(flow);
          setCurrentPhase('assessment');

          const firstQuestion = {
            type: 'bot',
            content: flow[0].question,
            isQuestion: true,
            field: flow[0].field,
            suggestions: flow[0].suggestions
          };
          setMessages(prev => [...prev, firstQuestion]);
          setCurrentStep(0);
        }, 800);
      } else {
        // Regular request flow
        const welcomeMessage = {
          type: 'bot',
          content: `Great! I see you want to work with ${existingRec.recommender_name}.`,
          timestamp: Date.now()
        };

        const sliderMessage = {
          type: 'slider',
          content: `Now I need to understand your relationship with them. Use the slider below to show how well they know you.`,
          timestamp: Date.now() + 100,
          recommenderName: existingRec.recommender_name
        };

        setMessages([welcomeMessage, sliderMessage]);
      }
    } catch (error) {
      console.error('Error populating recommendation data:', error);
      // If population fails, fall back to new recommendation flow
      setNeedsRecommenderInfo(true);
    }
  };

  const loadExistingRecommendation = async () => {
    try {
      if (!recommenderId) {
        setNeedsRecommenderInfo(true);
        return;
      }

      // Load the existing recommendation data
      const existingRec = await enhancedRecommendationService.getRecommendationById(recommenderId);

      if (existingRec) {
        // Populate the form with existing data
        setTempRecommenderInfo({
          name: existingRec.recommender_name || '',
          email: existingRec.recommender_email || '',
          type: existingRec.recommender_type || 'teacher',
          subject: existingRec.subject || '',
            relationship_length: existingRec.relationship_length || '',
          colleges: existingRec.colleges || [],
          due_date: existingRec.due_date ?
            new Date(existingRec.due_date).toISOString().split('T')[0] :
            (() => {
              const date = new Date();
              date.setDate(date.getDate() + 45);
              return date.toISOString().split('T')[0];
            })(),
          notes: existingRec.notes || ''
        });

        // Skip the info collection
        setNeedsRecommenderInfo(false);

        // For follow-ups and thank yous, skip relationship assessment
        if (emailType === 'followup' || emailType === 'thankyou') {
          let welcomeContent;
          if (emailType === 'followup') {
            welcomeContent = `Perfect! Let's draft the follow-up email to ${existingRec.recommender_name} with your materials.`;
          } else {
            welcomeContent = `Wonderful! Let's create a thoughtful thank you note for ${existingRec.recommender_name}.`;
          }

          const welcomeMessage = {
            type: 'bot',
            content: welcomeContent,
            timestamp: Date.now()
          };

          setMessages([welcomeMessage]);
          setSliderConfirmed(true); // Skip slider
          setRelationshipLevel(75); // Default to medium-high for follow-ups

          // Start conversation flow directly
          setTimeout(() => {
            const assessment = generateAiAssessment();
            setAiAssessment(assessment);

            const flow = emailType === 'followup' ? conversationFlows.followup : conversationFlows.thankyou;
            setConversationFlow(flow);
            setCurrentPhase('assessment');

            const firstQuestion = {
              type: 'bot',
              content: flow[0].question,
              isQuestion: true,
              field: flow[0].field,
              suggestions: flow[0].suggestions
            };
            setMessages(prev => [...prev, firstQuestion]);
            setCurrentStep(0);
          }, 800);
        } else {
          // Regular request flow
          const welcomeMessage = {
            type: 'bot',
            content: `Great! I see you want to work with ${existingRec.recommender_name}. Let me understand your relationship with them using the slider below.`,
            timestamp: Date.now()
          };

          const sliderMessage = {
            type: 'slider',
            content: `Use the slider below to show how well they know you.`,
            timestamp: Date.now() + 100,
            recommenderName: existingRec.recommender_name
          };

          setMessages([welcomeMessage, sliderMessage]);
        }
      } else {
        setNeedsRecommenderInfo(true);
      }
    } catch (error) {
      console.error('Error loading existing recommendation:', error);
      // If loading fails, fall back to new recommendation flow
      setNeedsRecommenderInfo(true);
    }
  };

  const generateAiAssessment = () => {
    const relationshipStrength = relationshipLevel <= 40 ? 'low' : 
                                relationshipLevel <= 75 ? 'medium' : 'high';
    
    const recommenderType = tempRecommenderInfo.type === 'coach' ? 'coach_advisor' :
                           tempRecommenderInfo.type === 'other' ? 'employer_mentor' :
                           tempRecommenderInfo.type;

    const assessment = {
      recommenderType,
      relationshipStrength,
      relationshipLevel,
      suggestedApproach: relationshipStrength === 'high' ? 
        'Focus on specific moments and personal growth they\'ve witnessed' :
        relationshipStrength === 'medium' ?
        'Highlight consistent qualities and memorable interactions' :
        'Emphasize clear examples of your work and character',
      confidenceScore: 0.85,
      keyInsights: [
        `${relationshipStrength} relationship suggests ${relationshipStrength === 'high' ? 'detailed personal examples' : 'clear, specific examples'}`,
        `${recommenderType} perspective offers unique view of your ${recommenderType === 'teacher' ? 'academic abilities' : recommenderType === 'counselor' ? 'overall development' : recommenderType === 'coach_advisor' ? 'character and leadership' : 'work ethic and maturity'}`
      ]
    };

    setAiAssessment(assessment);
    return assessment;
  };

  const getConversationFlow = () => {
    if (!aiAssessment) return [];
    
    const { recommenderType, relationshipStrength } = aiAssessment;
    return conversationFlows[recommenderType]?.[relationshipStrength] ||
           conversationFlows[recommenderType]?.medium ||
           conversationFlows.teacher.medium;
  };

  // RecommenderInfoSummary component
  const RecommenderInfoSummary = () => {
    const [isEditing, setIsEditing] = useState(false);
    const [editData, setEditData] = useState({
      name: tempRecommenderInfo.name || '',
      email: tempRecommenderInfo.email || '',
      subject: tempRecommenderInfo.subject || '',
      relationship_length: tempRecommenderInfo.relationship_length || '',
      due_date: tempRecommenderInfo.due_date || ''
    });

    useEffect(() => {
      setEditData({
        name: tempRecommenderInfo.name || '',
        email: tempRecommenderInfo.email || '',
        subject: tempRecommenderInfo.subject || '',
        relationship_length: tempRecommenderInfo.relationship_length || '',
        due_date: tempRecommenderInfo.due_date || ''
      });
    }, [tempRecommenderInfo]);

    const handleSaveEdit = () => {
      setTempRecommenderInfo(prev => ({
        ...prev,
        ...editData
      }));
      setIsEditing(false);
    };

    const handleCancelEdit = () => {
      setEditData({
        name: tempRecommenderInfo.name || '',
        email: tempRecommenderInfo.email || '',
        subject: tempRecommenderInfo.subject || '',
        relationship_length: tempRecommenderInfo.relationship_length || '',
        due_date: tempRecommenderInfo.due_date || ''
      });
      setIsEditing(false);
    };

    // Don't show if no data is available
    if (!tempRecommenderInfo.name && !tempRecommenderInfo.email) {
      return null;
    }

    return (
      <div className="recommender-info-summary compact">
        {isEditing ? (
          <div className="edit-mode">
            <div className="edit-grid">
              <div className="edit-field">
                <label>Teacher Name</label>
                <input
                  type="text"
                  value={editData.name}
                  onChange={(e) => setEditData(prev => ({...prev, name: e.target.value}))}
                  placeholder="Teacher's name"
                />
              </div>
              <div className="edit-field">
                <label>Email</label>
                <input
                  type="email"
                  value={editData.email}
                  onChange={(e) => setEditData(prev => ({...prev, email: e.target.value}))}
                  placeholder="Teacher's email"
                />
              </div>
              <div className="edit-field">
                <label>Subject/Activity</label>
                <input
                  type="text"
                  value={editData.subject}
                  onChange={(e) => setEditData(prev => ({...prev, subject: e.target.value}))}
                  placeholder="e.g., AP Biology, Math Team"
                />
              </div>
              <div className="edit-field">
                <label>How long have they known you?</label>
                <input
                  type="text"
                  value={editData.relationship_length}
                  onChange={(e) => setEditData(prev => ({...prev, relationship_length: e.target.value}))}
                  placeholder="e.g., 2 years, 1 semester"
                />
              </div>
              <div className="edit-field">
                <label>Deadline</label>
                <input
                  type="date"
                  value={editData.due_date}
                  onChange={(e) => setEditData(prev => ({...prev, due_date: e.target.value}))}
                />
              </div>
            </div>
            <div className="edit-actions">
              <button onClick={handleSaveEdit} className="save-btn">Save</button>
              <button onClick={handleCancelEdit} className="cancel-btn">Cancel</button>
            </div>
          </div>
        ) : (
          <div className="display-mode compact">
            <div className="info-line">
              <span className="info-name">{tempRecommenderInfo.name}</span>
              {tempRecommenderInfo.subject && (
                <>
                  <span className="separator">•</span>
                  <span>{tempRecommenderInfo.subject}</span>
                </>
              )}
              {tempRecommenderInfo.relationship_length && (
                <>
                  <span className="separator">•</span>
                  <span>{tempRecommenderInfo.relationship_length}</span>
                </>
              )}
              {tempRecommenderInfo.due_date && (
                <>
                  <span className="separator">•</span>
                  <span className="deadline">
                    Due: {new Date(tempRecommenderInfo.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </span>
                </>
              )}
            </div>
            <button onClick={() => setIsEditing(true)} className="edit-btn-inline">
              <Edit2 className="w-3 h-3" />
              Edit
            </button>
          </div>
        )}
      </div>
    );
  };

  const handleEditResponse = (messageIndex) => {
    const message = messages[messageIndex];
    if (message && message.type === 'user') {
      setInputValue(message.content);
      setEditingMessageIndex(messageIndex);
      inputRef.current?.focus();
    }
  };

  const handleResponse = (response, isFromSuggestion = false) => {
    // Handle editing existing response
    if (editingMessageIndex !== null) {
      // Update the message
      const updatedMessages = [...messages];
      updatedMessages[editingMessageIndex] = {
        ...updatedMessages[editingMessageIndex],
        content: response
      };
      setMessages(updatedMessages);

      // Find which question this was for and update responses
      const editedMessage = messages[editingMessageIndex];
      let questionIndex = -1;
      let questionField = null;

      // Find the bot message before this user message to determine which question
      for (let i = editingMessageIndex - 1; i >= 0; i--) {
        const msg = messages[i];
        if (msg.type === 'bot' && msg.field) {
          questionField = msg.field;
          questionIndex = conversationFlow.findIndex(q => q.field === msg.field);
          break;
        }
      }

      if (questionField) {
        // Update responses and personal context
        const newResponses = { ...responses, [questionField]: response };
        setResponses(newResponses);

        const question = conversationFlow.find(q => q.field === questionField);
        if (question) {
          const newPersonalContext = { ...personalContext };
          if (question.templateMapping) {
            newPersonalContext[question.templateMapping] = response;
          }
          newPersonalContext[question.field] = response;
          setPersonalContext(newPersonalContext);
        }
      }

      setInputValue('');
      setEditingMessageIndex(null);
      return;
    }

    const currentQuestion = conversationFlow[currentStep];
    if (!currentQuestion) return;

    // Check if this is an optional question and user wants to skip
    const isSkipping = currentQuestion.optional &&
                      (response === "Nope! Let's keep it short and sweet." ||
                       response === "Not really");

    // Save response (unless skipping)
    if (!isSkipping) {
      const newResponses = {
        ...responses,
        [currentQuestion.field]: response
      };
      setResponses(newResponses);

      // Update personal context for template mapping
      const newPersonalContext = { ...personalContext };
      if (currentQuestion.templateMapping) {
        newPersonalContext[currentQuestion.templateMapping] = response;
      }
      newPersonalContext[currentQuestion.field] = response;
      setPersonalContext(newPersonalContext);
    }

    // Add user message
    const userMessage = {
      type: 'user',
      content: response,
      timestamp: Date.now()
    };
    setMessages(prev => [...prev, userMessage]);

    // Clear input
    setInputValue('');

    // Move to next question or finish
    if (currentStep < conversationFlow.length - 1 && !isSkipping) {
      const nextStep = currentStep + 1;
      const nextQuestion = conversationFlow[nextStep];

      setTimeout(() => {
        const botMessage = {
          type: 'bot',
          content: nextQuestion.question,
          timestamp: Date.now(),
          suggestions: nextQuestion.suggestions,
          field: nextQuestion.field
        };
        setMessages(prev => [...prev, botMessage]);
        setCurrentStep(nextStep);
      }, 500);
    } else {
      // Conversation complete, move to template phase
      console.log('✅ Conversation complete! Moving to template phase...');
      setTimeout(() => {
        moveToTemplatePhase();
      }, 500);
    }
  };

  // Removed example confirmation functions - no longer needed with direct button population

  const moveToTemplatePhase = () => {
    console.log('📋 Moving to template phase...');
    console.log('AI Assessment:', aiAssessment);
    console.log('Personal Context:', personalContext);
    
    setCurrentPhase('template');
    
    // Generate framework guidance
    const template = emailTemplates[aiAssessment.recommenderType] || emailTemplates.teacher;
    const timelineWarning = getTimelineWarning(tempRecommenderInfo.due_date);
    
    setSelectedTemplate(template);
    setFrameworkGuidance({
      template,
      timelineWarning,
      strategicTips: recommendationStrategy.selection_criteria[
        aiAssessment.recommenderType === 'coach_advisor' ? 'character_recommenders' :
        aiAssessment.recommenderType === 'counselor' ? 'counselor' : 'academic_recommenders'
      ]
    });

    const transitionMessage = {
      type: 'bot',
      content: `Got it! Give me a sec to put this together...`,
      timestamp: Date.now()
    };

    setMessages(prev => [...prev, transitionMessage]);
    
    console.log('⏳ Waiting 1 second before generating email...');
    setTimeout(() => {
      console.log('🚀 Calling generatePersonalizedEmail()...');
      generatePersonalizedEmail();
    }, 1000);
  };

  const generatePersonalizedEmail = async () => {
    console.log('🔄 Starting email generation...');
    setIsGeneratingEmail(true);
    
    try {
      // Create the enhanced recommendation data structure
      const enhancedData = {
        ...tempRecommenderInfo,
        recommender_type: aiAssessment.recommenderType,
        ai_assessment: aiAssessment,
        personal_context: personalContext,
        due_date: new Date(tempRecommenderInfo.due_date),
        user_name: currentUser?.name || 'Student',
        user_id: currentUser?.id
      };

      console.log('📤 Calling enhancedRecommendationService with data:', {
        recommender_type: enhancedData.recommender_type,
        ai_assessment: enhancedData.ai_assessment,
        personal_context_keys: Object.keys(enhancedData.personal_context),
        due_date: enhancedData.due_date
      });

      // Generate personalized email using the enhanced service
      const emailResult = await enhancedRecommendationService.generatePersonalizedEmail(
        enhancedData,
        emailType || 'request'
      );
      
      console.log('✅ Email generation successful:', emailResult);
      
      setGeneratedEmail(emailResult.body);
      setEditableEmail(emailResult.body);
      setCurrentPhase('email');
      
      const emailMessage = {
        type: 'bot',
        content: `Here's your email! Feel free to edit it or copy as is.`,
        timestamp: Date.now()
      };
      
      setMessages(prev => [...prev, emailMessage]);
      
    } catch (error) {
      console.error('❌ Error generating email:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      toast.error('Failed to generate email. Check console for details.');
    } finally {
      setIsGeneratingEmail(false);
    }
  };

  const handleCopyEmail = async () => {
    try {
      const fullEmail = `Subject: Recommendation Letter Request - ${currentUser?.name || '[Your Name]'}\n\n${editableEmail}`;
      await navigator.clipboard.writeText(fullEmail);
      toast.success('✅ Email copied to clipboard!');
    } catch (error) {
      toast.error('Failed to copy to clipboard');
    }
  };

  const handleSaveDraft = async () => {
    try {
      // Create the recommendation in the database
      const conversationResponses = Object.entries(responses).map(([field, response]) => ({
        questionId: field,
        question: conversationFlow.find(q => q.field === field)?.question || '',
        response,
        field,
        timestamp: new Date()
      }));

      const recommendationInfo = {
        recommender_name: tempRecommenderInfo.name,
        recommender_email: tempRecommenderInfo.email,
        subject: tempRecommenderInfo.subject,
        relationship_length: tempRecommenderInfo.relationship_length,
        due_date: new Date(tempRecommenderInfo.due_date),
        colleges: tempRecommenderInfo.colleges,
        notes: tempRecommenderInfo.notes
      };

      let enhancedRecommendation;

      // Check if we're updating an existing recommendation or creating a new one
      if (recommenderId) {
        // Update existing recommendation
        enhancedRecommendation = await enhancedRecommendationService.updateSmartRecommendation(
          recommenderId,
          aiAssessment,
          conversationResponses,
          personalContext,
          recommendationInfo
        );
      } else {
        // Create new recommendation
        enhancedRecommendation = await enhancedRecommendationService.createSmartRecommendation(
          currentUser.id,
          aiAssessment,
          conversationResponses,
          personalContext,
          recommendationInfo
        );
      }

      // Save the generated email
      await enhancedRecommendationService.saveEnhancedEmail(
        enhancedRecommendation.id,
        'request',
        editableEmail,
        `Recommendation Letter Request - ${currentUser?.name || '[Your Name]'}`,
        ['AI-generated', 'Framework-integrated', 'Personalized'],
        'draft'
      );

      // Mark draft as completed
      await recommendationLetterService.updateRecommendation(enhancedRecommendation.id, {
        draft_completed: true,
        draft_completed_date: new Date().toISOString()
      });

      toast.success('Draft saved successfully! 📋', {
        duration: 3000,
        icon: '✅'
      });

      // Close the smart flow and return to recommendation management
      setTimeout(() => {
        onComplete?.(enhancedRecommendation);
      }, 1000);

    } catch (error) {
      console.error('Error saving draft:', error);
      toast.error('Failed to save draft. Please try again.');
    }
  };

  const handleDownloadDraft = () => {
    const emailContent = `Subject: Recommendation Letter Request - ${currentUser?.name || '[Your Name]'}\n\n${editableEmail}`;
    const blob = new Blob([emailContent], { type: 'application/msword' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `recommendation-request-${tempRecommenderInfo.name?.replace(/\s+/g, '-') || 'draft'}.doc`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Email downloaded! 📥');
  };

  const handleEmailToSelf = () => {
    const subject = encodeURIComponent(`Recommendation Letter Request - ${currentUser?.name || '[Your Name]'}`);
    const body = encodeURIComponent(editableEmail);
    const mailtoUrl = `mailto:${currentUser?.email || ''}?subject=${subject}&body=${body}`;
    window.open(mailtoUrl, '_blank');
    toast.success('Email app opened! 📧');
  };

  // Rest of the component rendering logic would be similar to the original SmartRecRequestFlow
  // but with enhanced UI for showing framework integration, timeline warnings, etc.
  
  const renderRecommenderInfoCollection = () => {
    // Skip name field if it's already populated from an existing recommendation
    const baseSteps = [
      { field: 'name', label: "What's your recommender's name?", placeholder: "Ms. Johnson", type: 'text' },
      { field: 'email', label: "What's their email address? (optional)", placeholder: "teacher@school.edu", type: 'email', optional: true },
      { field: 'type', label: "What's their role?", type: 'select', options: [
        { value: 'teacher', label: 'Teacher' },
        { value: 'counselor', label: 'School Counselor' },
        { value: 'coach', label: 'Coach/Advisor' },
        { value: 'other', label: 'Employer/Mentor' }
      ]},
      { field: 'subject', label: "What subject/activity?", placeholder: "AP Biology, Math Team, etc.", type: 'text' },
      { field: 'relationship_length', label: "How long have they known you?", placeholder: "2 years, 1 semester, etc.", type: 'text' },
    ];

    // If we already have a name from an existing recommendation, skip that step
    const steps = tempRecommenderInfo.name && recommenderId ?
      baseSteps.filter(step => step.field !== 'name') :
      baseSteps;

    const currentStepData = steps[infoCollectionStep];
    if (!currentStepData) return null;

    return (
      <div className="recommender-info-form">
        <h3 className="text-lg font-semibold mb-4 text-slate-900">
          Let's get started - tell me about your recommender
        </h3>
        <div className="form-group">
          <label htmlFor={currentStepData.field}>{currentStepData.label}</label>
          {currentStepData.type === 'select' ? (
            <select
              className="form-select"
              value={tempRecommenderInfo[currentStepData.field] || ''}
              onChange={(e) => setTempRecommenderInfo(prev => ({
                ...prev,
                [currentStepData.field]: e.target.value
              }))}
            >
              {currentStepData.options?.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          ) : (
            <input
              type={currentStepData.type}
              className="form-input"
              placeholder={currentStepData.placeholder}
              value={tempRecommenderInfo[currentStepData.field] || ''}
              onChange={(e) => setTempRecommenderInfo(prev => ({
                ...prev,
                [currentStepData.field]: e.target.value
              }))}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && tempRecommenderInfo[currentStepData.field]) {
                  handleInfoStepNext();
                }
              }}
            />
          )}
        </div>
        <button
          className="submit-btn"
          disabled={!currentStepData.optional && !tempRecommenderInfo[currentStepData.field]}
          onClick={handleInfoStepNext}
        >
          {infoCollectionStep === steps.length - 1 ? 'Start Assessment' : 'Next'}
        </button>
      </div>
    );
  };

  const handleInfoStepNext = () => {
    // Calculate the number of steps dynamically
    const steps = tempRecommenderInfo.name && recommenderId ? 4 : 5; // 4 steps if name is pre-filled, 5 otherwise

    if (infoCollectionStep < steps - 1) {
      setInfoCollectionStep(infoCollectionStep + 1);
    } else {
      // Add collected recommender info to responses for profile display
      const recommenderInfoResponses = {};
      if (tempRecommenderInfo.name) recommenderInfoResponses.recommender_name = tempRecommenderInfo.name;
      if (tempRecommenderInfo.email) recommenderInfoResponses.recommender_email = tempRecommenderInfo.email;
      if (tempRecommenderInfo.type) recommenderInfoResponses.recommender_type = tempRecommenderInfo.type;
      if (tempRecommenderInfo.subject) recommenderInfoResponses.subject = tempRecommenderInfo.subject;
      if (tempRecommenderInfo.relationship_length) recommenderInfoResponses.relationship_length = tempRecommenderInfo.relationship_length;

      setResponses(recommenderInfoResponses);
      setNeedsRecommenderInfo(false);

      // Add welcome message and slider as messages in conversation flow
      const welcomeMessage = {
        type: 'bot',
        content: `Great! Now let's talk about your relationship with ${tempRecommenderInfo.name}.`,
        timestamp: Date.now()
      };

      const sliderMessage = {
        type: 'slider',
        content: `Use the slider below to show how well they know you.`,
        timestamp: Date.now() + 100,
        recommenderName: tempRecommenderInfo.name
      };

      setMessages([welcomeMessage, sliderMessage]); // Start fresh messages after info collection
    }
  };

  const handleSliderChange = (value) => {
    setRelationshipLevel(value);

    // Generate dynamic feedback
    let feedback = '';
    if (value <= 40) {
      feedback = "Sounds like a newer relationship. Does that sound right?";
    } else if (value <= 75) {
      feedback = "Sounds like you know each other pretty well. Does that sound right?";
    } else {
      feedback = "Sounds like you have a close relationship. Does that sound right?";
    }

    setSliderFeedback(feedback);

    // Update existing feedback message or create new one
    if (feedbackMessageIndex !== null) {
      // Update existing feedback message
      setMessages(prev => prev.map((msg, index) =>
        index === feedbackMessageIndex
          ? { ...msg, content: feedback, relationshipLevel: value }
          : msg
      ));
    } else {
      // Create new feedback message and track its index
      const feedbackMessage = {
        type: 'slider_feedback',
        content: feedback,
        timestamp: Date.now(),
        relationshipLevel: value
      };

      setMessages(prev => {
        const newMessages = [...prev, feedbackMessage];
        setFeedbackMessageIndex(newMessages.length - 1);
        return newMessages;
      });
    }

    setShowSliderConfirmation(true);
  };

  const confirmSliderAssessment = () => {
    setSliderConfirmed(true);
    setShowSliderConfirmation(false);

    // Add user confirmation message
    const confirmationResponse = {
      type: 'user',
      content: "Yes, that's right",
      timestamp: Date.now()
    };

    setMessages(prev => [...prev, confirmationResponse]);

    // Generate assessment and start conversation flow
    const assessment = generateAiAssessment();
    setAiAssessment(assessment);

    // Select flow based on email type
    let flow;
    if (emailType === 'followup') {
      flow = conversationFlows.followup;
    } else if (emailType === 'thankyou') {
      flow = conversationFlows.thankyou;
    } else {
      flow = conversationFlows[assessment.recommenderType]?.[assessment.relationshipStrength] ||
                  conversationFlows[assessment.recommenderType]?.medium ||
                  conversationFlows.teacher.medium;
    }

    setConversationFlow(flow);
    setCurrentPhase('assessment');

    // Add assessment message with delay
    setTimeout(() => {
      let assessmentContent;
      if (emailType === 'followup') {
        assessmentContent = `Great! Let's prepare your follow-up email with your materials. Just ${flow.length} quick questions.`;
      } else if (emailType === 'thankyou') {
        assessmentContent = `Wonderful! Let's craft a thoughtful thank you note. Just ${flow.length} quick questions.`;
      } else {
        assessmentContent = `Cool! Let's make this quick - just ${flow.length} questions.`;
      }

      const assessmentMessage = {
        type: 'bot',
        content: assessmentContent,
        timestamp: Date.now()
      };
      setMessages(prev => [...prev, assessmentMessage]);

      // Add first question with slight delay
      setTimeout(() => {
        const firstQuestion = {
          type: 'bot',
          content: flow[0].question,
          timestamp: Date.now(),
          suggestions: flow[0].suggestions,
          field: flow[0].field
        };
        setMessages(prev => [...prev, firstQuestion]);
      }, 800);
    }, 500);

    setCurrentStep(0);
  };

  return (
    <div className="smart-rec-request-flow">
      <div className="container h-full">
        {/* Chat Side */}
        <div className="chat-side">
          {/* Header */}
          <div className="chat-header">
            <button className="back-btn" onClick={onBack}>
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div className="header-content">
              <h2 className="chat-title">Recommendation Assistant</h2>

</div>

</div><div>

          {/* Step Indicator Bar */}
                      {/* Simplified Progress Dots */}
                      <div className="progress-dots bg-slate-800 pb-5 mx-auto justify-center">
              <div className={`progress-dot ${currentPhase === 'assessment' ? 'active' : currentPhase === 'template' || currentPhase === 'email' ? 'completed' : ''}`}>
                1
              </div>
              <div className="progress-connector" />
              <div className={`progress-dot ${currentPhase === 'template' ? 'active' : currentPhase === 'email' ? 'completed' : ''}`}>
                2
              </div>
              <div className="progress-connector" />
              <div className={`progress-dot ${currentPhase === 'email' ? 'active' : ''}`}>
                3
              </div>
            </div>
            </div>
          <div className="step-indicator-bar">
            Step {currentPhase === 'assessment' ? '1' : currentPhase === 'template' || currentPhase === 'email' ? '2' : '1'} of 3 •
            {currentPhase === 'assessment' ? ' Assessment' : currentPhase === 'template' || currentPhase === 'email' ? ' Email Draft' : ' Getting Started'}
          </div>


          {/* Messages */}
          <div className="chat-messages" ref={chatMessagesRef}>
            {/* Spacer to push messages down */}
            <div className="messages-spacer" />

            {/* Welcome Message - Always at top */}
            {needsRecommenderInfo && (
              <div className="message">
                <div className="bot-message">
                  <div className="bot-avatar">
                    <Bot className="w-4 h-4 text-white" />
                  </div>
                  <div className="bot-message-content">
                    Hey! I'm your recommendation letter assistant. Let's create a personalized request that gets results. First, I need some basic info about your recommender.
                  </div>
                </div>
              </div>
            )}

            {/* Info Collection Phase */}
            {needsRecommenderInfo && renderRecommenderInfoCollection()}

            {/* Chat Messages */}
            {messages.map((message, index) => (
              <div
                key={index}
                className="message"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {message.type === 'bot' ? (
                  <div className="bot-message">
                    <div className="bot-avatar">
                      <Bot className="w-4 h-4 text-white" />
                    </div>
                    <div className="bot-message-content">
                      {message.content}
                    </div>
                  </div>
                ) : message.type === 'slider' ? (
                  <div className="bot-message">
                    <div className="bot-avatar">
                      <Bot className="w-4 h-4 text-white" />
                    </div>
                    <div className="bot-message-content">
                      <div className="message-text">{message.content}</div>
                      <div className="relationship-slider-message">
                        <div className="slider-label">
                          How well does {message.recommenderName || 'your recommender'} know you?
                        </div>
                        <div className="slider-container">
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={relationshipLevel}
                            onChange={(e) => handleSliderChange(parseInt(e.target.value))}
                            className="slider"
                          />
                        </div>
                        <div className="slider-labels">
                          <span>Just started working together</span>
                          <span>Know each other pretty well</span>
                          <span>Close mentor relationship</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : message.type === 'slider_feedback' ? (
                  <div className="bot-message">
                    <div className="bot-avatar">
                      <Bot className="w-4 h-4 text-white" />
                    </div>
                    <div className="bot-message-content">
                      <div className="mb-3 p-3 bg-azure-50 border border-azure-200 rounded-lg">
                        <p className="text-azure-800 text-sm font-medium">{message.content}</p>
                      </div>
                      <button
                        onClick={confirmSliderAssessment}
                        className="px-6 py-2 bg-gradient-to-r from-violet-600 to-azure-600 text-white rounded-xl hover:from-violet-700 hover:to-azure-700 transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 flex items-center gap-2 mx-auto"
                      >
                        <CheckCircle className="w-4 h-4" />
                        Yes, that's right
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="student-message">
                    <div className="student-message-content">
                      {message.content}
                      <button
                        onClick={() => handleEditResponse(index)}
                        className="edit-response-btn"
                        title="Edit this response"
                      >
                        <Edit3 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}

            {/* Enhanced Loading indicator */}
            {isGeneratingEmail && (
              <div className="message">
                <div className="bot-message">
                  <div className="bot-avatar">
                    <div className="animated-loader-icon">
                      <Sparkles className="w-4 h-4 text-white" />
                    </div>
                  </div>
                  <div className="bot-message-content">
                    <div className="space-y-3">
                      <div className="font-medium text-azure-800 flex items-center gap-2">
                        <div className="typing-dots">
                          <span></span>
                          <span></span>
                          <span></span>
                        </div>
                        Creating your personalized email...
                      </div>
                      <div className="text-sm text-slate-600">
                        ✨ Applying AI insights<br/>
                        📋 Using strategic framework<br/>
                        🎯 Personalizing with your context
                      </div>
                      <div className="w-full bg-azure-200 rounded-full h-2 overflow-hidden">
                        <div className="bg-azure-600 h-2 rounded-full animated-progress"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Input Area */}
          {!needsRecommenderInfo && sliderConfirmed && currentStep < conversationFlow.length && !isGeneratingEmail && (
            <div className="input-area">
              {/* Quick Response Buttons */}
              {conversationFlow[currentStep]?.suggestions && (
                <div className="quick-response-buttons">
                  {conversationFlow[currentStep].suggestions.map((suggestion, i) => (
                    <button
                      key={i}
                      className="quick-response-btn"
                      onClick={() => setInputValue(suggestion)}
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              )}

              <div className="input-container">
                <textarea
                  ref={inputRef}
                  className="chat-input"
                  placeholder={editingMessageIndex !== null ? "Edit your previous response..." : "Type your response or click a suggestion above..."}
                  value={inputValue}
                  onChange={(e) => {
                    setInputValue(e.target.value);
                    // Auto-resize the textarea
                    e.target.style.height = 'auto';
                    e.target.style.height = Math.min(e.target.scrollHeight, 150) + 'px';
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      if (inputValue.trim()) {
                        handleResponse(inputValue.trim());
                        // Reset height after sending
                        if (inputRef.current) {
                          inputRef.current.style.height = 'auto';
                        }
                      }
                    }
                  }}
                  rows={1}
                />
                <button
                  className={`send-btn ${inputValue.trim() ? 'active' : ''}`}
                  onClick={() => {
                    if (inputValue.trim()) {
                      handleResponse(inputValue.trim());
                      // Reset height after sending
                      if (inputRef.current) {
                        inputRef.current.style.height = 'auto';
                      }
                    }
                  }}
                  disabled={!inputValue.trim()}
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
              <div className={`char-counter ${inputValue.length > 140 ? 'over' : ''}`}>
                {inputValue.length}/140 characters
              </div>
            </div>
          )}
        </div>

        {/* Email Preview Side - Enhanced */}
        <div className="email-side">
          <div className="email-header">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="email-title">
                  {currentPhase === 'assessment' ? 'Building Your Profile' :
                   currentPhase === 'template' ? 'Applying Framework' : 'Recommendation Letter Request'}
                </h3>
                <p className="email-subtitle">
                  {currentPhase === 'assessment' ? 'Gathering context for personalization' :
                   currentPhase === 'template' ? 'Integrating strategic template' : 'Ready to copy and send'}
                </p>
              </div>
              {currentPhase === 'template' && (
                <div className="flex items-center gap-2 text-xs text-slate-600">
                  <Award className="w-3 h-3" />
                  Framework
                </div>
              )}
            </div>
          </div>

          {/* Progress Bar */}
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{
                width: `${currentPhase === 'assessment' ? (currentStep / Math.max(conversationFlow.length, 1)) * 60 :
                        currentPhase === 'template' ? 80 : 100}%`
              }}
            />
          </div>

          {/* Recommender Info Summary */}
          <RecommenderInfoSummary />

          <div className="email-content">
            {currentPhase === 'assessment' && (
              <div className="placeholder-email">
                <div className="mb-4">
                  <div className="email-label">Building your profile...</div>
                  <div className="text-sm text-slate-600">
                    {Object.keys(responses).length > 0 && (
                      <div className="space-y-2">
                        {Object.entries(responses).map(([field, response]) => (
                          <div key={field} className="bg-white p-3 rounded border-l-4 border-teal-500">
                            <div className="font-medium text-xs text-teal-700 uppercase tracking-wide">
                              {field.replace(/_/g, ' ')}
                            </div>
                            <div className="text-slate-700 mt-1">{response}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {currentPhase === 'template' && frameworkGuidance && (
              <div className="space-y-4">
                <div className="bg-azure-50 border border-azure-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="w-4 h-4 text-azure-600" />
                    <span className="font-semibold text-azure-900">Framework Integration</span>
                  </div>
                  <p className="text-azure-800 text-sm">
                    Applying {aiAssessment.recommenderType} template with {aiAssessment.relationshipStrength} relationship customizations...
                  </p>
                </div>

                {frameworkGuidance.timelineWarning && (
                  <div className={`border rounded-lg p-4 ${
                    frameworkGuidance.timelineWarning.type === 'error' ? 'bg-red-50 border-red-200' :
                    frameworkGuidance.timelineWarning.type === 'warning' ? 'bg-yellow-50 border-yellow-200' :
                    'bg-emerald-50 border-emerald-200'
                  }`}>
                    <div className="flex items-center gap-2 mb-1">
                      <Clock className="w-4 h-4" />
                      <span className="font-semibold text-sm">Timeline Analysis</span>
                    </div>
                    <p className="text-xs">{frameworkGuidance.timelineWarning.message}</p>
                  </div>
                )}
              </div>
            )}

            {currentPhase === 'email' && generatedEmail && (
              <div className="email-draft">
                <div className="email-field flex flex-row justify-between align-middle gap-2">
                  <div className="email-label flex">Subject</div>
                  <div className="email-value flex flex-grow">
                    Recommendation Letter Request - {currentUser?.name || '[Your Name]'}
                  </div>
                </div>
                <div className="email-field">
                  <div className="email-label-container">
                    <div className="email-label">Message</div>
                    <div className="email-tools">
                      <button
                        onClick={() => setEditableEmail(generatedEmail)}
                        className="reset-to-ai-btn"
                        title="Reset to AI version"
                      >
                        ↻ Reset to AI version
                      </button>
                      <span className="char-count">
                        {editableEmail.length} characters
                      </span>
                    </div>
                  </div>
                  <textarea
                    value={editableEmail}
                    onChange={(e) => setEditableEmail(e.target.value)}
                    className="email-message-textarea"
                    rows="16"
                    placeholder="Edit your email message here..."
                  />
                </div>
                
                <div className="email-actions">
                  <div className="action-buttons-row">
                    <button
                      onClick={handleSaveDraft}
                      className="action-btn primary"
                    >
                      <FileText className="w-4 h-4" />
                      Save
                    </button>
                    <button onClick={handleCopyEmail} className="action-btn">
                      <Copy className="w-4 h-4" />
                      Copy
                    </button>
                    <button onClick={handleDownloadDraft} className="action-btn">
                      <Download className="w-4 h-4" />
                      Download
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnhancedSmartRecRequestFlow;